/* ILS — Email popup logic. TASK-2065, TASK-2360, TASK-2432, TASK-2450, TASK-5977,
   TASK-5998, TASK-5999, TASK-6005.

   TASK-6005 — THE OPEN'S PROVENANCE (promo source 4).

   There is still exactly ONE popup, one email field and one submit flow. What
   TASK-6005 adds is a single page-scoped variable, `openOrigin`, which records
   WHY this instance of the popup is on screen:

     'popup'       it opened by itself (the 10 s automatic show) or through the
                   dev trigger — promo source 1, exactly as before;
     'pdp_widget'  the shopper pressed "Get code" on the product-page discount
                   widget (assets/custom-pdp-promo.js), which dispatches
                   'ils:email-popup:show' with detail.origin === 'pdp_widget' —
                   promo source 4.

   `openOrigin` is the value sent as `source` to /api/popup/email-status and
   /api/popup/promo-code, so the backend (ils-admin v1.0.291) puts the right
   ninth character in a NEWLY minted code and records the right originating
   source on an attempt that REUSES an older one. It is also published as the
   funnel context's `source`, so TASK-5998's rows separate a CTA-opened popup
   from an automatic one — on the same product page.

   THREE INVARIANTS, each of which is a defect if broken:

     * PAGE TYPE NEVER DECIDES THE SOURCE. An automatic popup on a product
       page is 'popup'. Only an explicit detail.origin from the widget is
       'pdp_widget', and only for the instance it opened.
     * THE MARK CANNOT LEAK INTO AN AUTOMATIC POPUP. It is reset to 'popup' on
       every close (X, backdrop, click-outside, Escape, the result panel's own
       X), when a submit resolves, at the top of the automatic attemptOpen
       path, and on pagehide / beforeunload.
     * IT IS NEVER PERSISTED. Not localStorage, not sessionStorage, not a
       cookie, and deliberately NOT window.ILSEmailSession — that store is
       shared across tabs, which is precisely how the mark would escape this
       page. It lives in this closure and dies with the page view.

   The funnel `form` key stays 'popup' everywhere, because the SURFACE is still
   the popup and the attempt id is keyed by form: re-keying it to 'pdp_widget'
   would split one interaction into two uncorrelated attempts. The ORIGIN
   travels in the context's `source`, which is the field the operator reads.

   TASK-5999 REPLACED THE WHOLE AUTO-SHOW POLICY. What it was, and what it is:

     | rule                | before (TASK-2450)        | now (TASK-5999)
     | eligible templates  | home only                 | home, product, collection, search
     | delay               | 8 s                       | 10 s
     | per-visit cap       | none                      | exactly ONE automatic show per visit
     | after a dismissal   | 14 days                   | 24 hours
     | lifetime cap        | 5 shows, then never again | none
     | after a code issued | popup submits only        | any of the THREE signup surfaces
     | storage             | 3 bare localStorage keys  | assets/custom-email-session.js

   The visit / dismissal / issued state is NOT owned here any more. It lives in
   window.ILSEmailSession (assets/custom-email-session.js), which the section
   tags immediately before this file, because the footer and homepage forms have
   to write the same "a code was issued" flag — see
   assets/custom-email-forms.js. Everything below degrades to a safe in-memory
   shim if that module is missing, which means "behave like a first visit for
   this page view" and never means "break the page".

   WHAT DELIBERATELY DID NOT CHANGE: the markup, the CSS, every string, the
   translations, the submit flow, the TASK-5977 typo / confirmation gate, the
   FO10 / SO05 decision, the two endpoints, the result panel, the
   ils-devcheck manual trigger ('ils:email-popup:show'), and every line of the
   TASK-5998 funnel telemetry — which gains the new gate reasons rather than
   losing the old ones.

   TASK-5977: step 0 of the submit handler is the SHARED email typo /
   confirmation gate (assets/custom-email-verify.js, window.ILSEmailVerify) —
   the same module, UI and localized strings the footer and homepage forms use.
   It runs before /api/popup/email-status, before the order-count lookup,
   before FO10/SO05 selection, before issuance and before any letter, and the
   popup's own dismissal / issued flags are not written until the address is
   settled. The server runs the same check authoritatively (409
   `email_unconfirmed`), handled below.
   - Auto-shows 10 s after load on the four eligible templates (data-enabled),
     at most once per visit, never within 24 h of a dismissal, never once a
     code has been issued to this browser.
   - Manual dev trigger via 'ils:email-popup:show' event (ils-devcheck-v2.js).
   - Email check (TASK-2360, extended TASK-2432):
       1. POST /api/popup/email-status → {status}
       2. For every status EXCEPT existing_multiple_orders, also POST
          /api/popup/promo-code with the resolved <LANG><MARKET> code and
          the discount type implied by the status (FO10 for new /
          existing_no_order, SO05 for existing_one_order). That endpoint
          returns {code, expires_at, ...} — we pass both into the shared
          renderer.
       3. On any network / non-2xx failure at any step → render the error
          state (state 5 in TASK-2432). No more silent "pretend it's new"
          fallback: the owner now wants users to see a real try-again
          message when the backend is unreachable.
   - All rendering is delegated to window.ILSEmailResult.render (shared
     with the footer + homepage inline forms — see custom-email-result.js).
*/
(function () {
  'use strict';

  /* TASK-5999 — one instance per page, whatever renders us. The section is
     emitted once by layout/theme.liquid, but a duplicated <script> tag (or a
     section re-render inside Theme Editor) must not arm a second 10-second
     timer against the same DOM. */
  if (window.__ilsEopBooted) return;
  window.__ilsEopBooted = true;

  /* TASK-5998 — funnel telemetry emitter. Fail-silent and order-independent:
     if assets/custom-funnel-telemetry.js has not executed yet the call is
     queued on window.ILSFunnelQ and drained when it does; if it never loads
     at all (404, blocked, throwing) every call here is a no-op and this form
     behaves exactly as it did before TASK-5998. It NEVER sends an address or a
     code value. */
  function ilsFn(stage, outcome, props) {
    try {
      if (window.ILSFunnel && window.ILSFunnel.event) {
        window.ILSFunnel.event(stage, outcome, props);
        return;
      }
      (window.ILSFunnelQ = window.ILSFunnelQ || []).push([stage, outcome, props]);
    } catch (e) {}
  }
  function ilsFnCtx(form, c) {
    try { if (window.ILSFunnel && window.ILSFunnel.ctx) window.ILSFunnel.ctx(form, c); } catch (e) {}
  }
  function ilsFnAttempt(form) {
    try {
      if (window.ILSFunnel && window.ILSFunnel.attempt) return window.ILSFunnel.attempt(form);
    } catch (e) {}
    return null;
  }
  function ilsFnObserve(el, form) {
    try { if (window.ILSFunnel && window.ILSFunnel.observeImpression) window.ILSFunnel.observeImpression(el, form); } catch (e) {}
  }

  /* TASK-6005 — the funnel SURFACE, which is a constant, as distinct from the
     request SOURCE, which is now per-open. `ILSFunnel.attempt()` is keyed by
     this, so one interaction is one attempt id whatever opened the popup. */
  var FORM = 'popup';

  var API_BASE = 'https://alert-dedication-production.up.railway.app';
  var STATUS_ENDPOINT = API_BASE + '/api/popup/email-status';
  var PROMO_ENDPOINT = API_BASE + '/api/popup/promo-code';

  /* TASK-5999 — ils_eop_suppress_until / ils_eop_submitted /
     ils_eop_show_count and their six accessors are GONE from this file. The
     keys still exist in visitors' browsers and are migrated once, by
     assets/custom-email-session.js, which now owns all of this state; see the
     migrate() note there for what each one becomes. */
  var DEFAULT_DISMISS_HOURS = 24;
  var DEFER_POLL_MS = 1000;   /* re-check interval while the moment is wrong */
  var DEFER_MAX_TRIES = 600;  /* ...for at most 10 minutes, then give up */

  /* The shared store, or a same-page-only shim with the identical surface.
     The shim is what a visitor gets if assets/custom-email-session.js has not
     loaded; it is not a second policy, just a store with no memory. */
  function session() {
    if (window.ILSEmailSession) return window.ILSEmailSession;
    if (!window.__ilsEopShim) {
      var st = { issued: false, dismissedAt: 0, shown: false, token: null };
      window.__ilsEopShim = {
        tier: 'none',
        state: function () {
          return { tier: 'none', issued: st.issued, issuedAt: 0, issuedSource: '',
            dismissedAt: st.dismissedAt, shownVisit: st.shown ? 'shim' : '',
            shownAt: 0, shownCount: 0, visitId: 'shim', visitStartedAt: 0 };
        },
        isIssued: function () { return st.issued; },
        markIssued: function () { st.issued = true; return true; },
        markDismissed: function () { st.dismissedAt = Date.now(); },
        dismissedWithin: function (ms) { return !!st.dismissedAt && (Date.now() - st.dismissedAt) < ms; },
        shownThisVisit: function () { return st.shown; },
        claimVisitShow: function () { if (st.shown) return null; st.shown = true; st.token = 'shim'; return 'shim'; },
        confirmVisitShow: function (t) { return t === st.token; },
        onChange: function () {},
        reset: function () { st.issued = false; st.dismissedAt = 0; st.shown = false; return true; }
      };
    }
    return window.__ilsEopShim;
  }

  function discountTypeFor(status) {
    if (status === 'existing_one_order') return 'SO05';
    return 'FO10';
  }

  /* TASK-5999 — the popup must never auto-open ON TOP of the cart drawer.
     The drawer is Dawn's <cart-drawer>, which carries .active exactly while it
     is open (assets/cart-drawer.js open()/close()). Requirement 1 bans the
     cart PAGE and the cart DRAWER: the page is handled by the section's
     template gate, the drawer can only be handled here, at fire time. */
  function cartDrawerOpen() {
    try {
      return !!(document.querySelector('cart-drawer.active')
             || document.querySelector('cart-drawer[open]')
             || document.querySelector('.cart-drawer.active'));
    } catch (e) { return false; }
  }

  /* A background tab would burn this visit's single appearance on a popup
     nobody ever saw. Wait for the tab to come forward instead. */
  function momentIsWrong() {
    try { if (document.hidden) return 'hidden'; } catch (e) {}
    if (cartDrawerOpen()) return 'cart_drawer_open';
    return null;
  }

  // TASK-2450 — prefix match against the current URL path.
  function isPathExcluded(excludeList) {
    if (!excludeList || !excludeList.length) return false;
    var currentPath = window.location.pathname || '/';
    for (var i = 0; i < excludeList.length; i++) {
      var p = excludeList[i];
      if (!p) continue;
      if (currentPath === p) return true;
      if (currentPath.indexOf(p) === 0) return true;
    }
    return false;
  }

  function parseExcludePaths(raw) {
    if (!raw) return [];
    // Theme Editor writes textarea contents with real newlines; the Liquid
    // `escape` filter turns them into &#13;&#10; entities. Browsers decode
    // those back to \r\n when we read the attribute via getAttribute, so we
    // just split on any newline.
    return raw.split(/\r?\n/).map(function (s) {
      return s.trim();
    }).filter(function (s) { return s.length > 0; });
  }

  function fetchJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('bad status ' + r.status);
      return r.json();
    });
  }

  // Full 2-step flow. Resolves to a `data` object suitable for
  // ILSEmailResult.render:
  //   {status:'new'|..., code?, expires_at?}
  // On any failure returns {status:'error'}.
  // TASK-5839 — ctx carries the raw localization facts the section read from
  // Shopify (country / language / market handle). The backend does not trust
  // the pre-computed combo on its own: it cross-checks it against the country
  // and repairs it, which is what a legacy /en-eu/ -> /en-de/ redirect needs.
  /* TASK-5977 - a 409 from the promo endpoint is NOT an error: it is the
     SERVER asking the same question the shared gate asks, and it is what makes
     the gate impossible to bypass. fetchJson throws on any non-2xx, so the 409
     body has to be read separately, before that happens. Identical helper to
     the one in custom-email-forms.js - both are three lines of transport, and
     the DECISION they carry lives in exactly one place (ILSEmailVerify). */
  function fetchPromo(body) {
    return fetch(PROMO_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (r.status === 409) {
        return r.json().then(function (j) { return { __unconfirmed: j || {} }; });
      }
      if (!r.ok) throw new Error('bad status ' + r.status);
      return r.json();
    });
  }

  function runFlow(email, source, marketCode, ctx, confirmed) {
    /* TASK-5998 — see the identical note in custom-email-forms.js. One
       page-scoped random UUID per interaction; no identity, no cookie. */
    /* TASK-6005 — keyed by FORM, not by `source`. `source` is now 'popup' or
       'pdp_widget'; keying the attempt by it would have given a CTA-opened
       submit its own attempt id, unjoinable to the popup_shown row that
       preceded it and to the server's api_issue / issue rows. */
    var attemptId = ilsFnAttempt(FORM);
    return fetchJson(STATUS_ENDPOINT, { email: email, source: source, attempt_id: attemptId })
      .then(function (statusResp) {
        var s = statusResp && statusResp.status;
        var valid = { 'new': 1, 'existing_no_order': 1, 'existing_one_order': 1, 'existing_multiple_orders': 1 };
        if (!valid[s]) throw new Error('bad status value');
        if (s === 'existing_multiple_orders') return { status: s };
        return fetchPromo({
          email: email,
          source: source,
          attempt_id: attemptId,
          lang_domain: marketCode,
          country: (ctx && ctx.country) || '',
          language: (ctx && ctx.language) || '',
          market_handle: (ctx && ctx.marketHandle) || '',
          /* TASK-5977 - only ever true when the visitor explicitly kept an
             address the gate had questioned. Never defaulted to true. */
          email_confirmed: confirmed === true,
          discount_type: discountTypeFor(s)
        }).then(function (promo) {
          if (promo && promo.__unconfirmed) return { status: 'unconfirmed', unconfirmed: promo.__unconfirmed };
          // TASK-5839 — a 200 no longer implies a code. The backend decides
          // eligibility from Shopify order history for EVERY address, so it
          // can answer {code:null, eligible:false} where the Klaviyo-derived
          // status said 'new'. Rendering that as a success is exactly how
          // REPORT-5836 C-2 produced an empty code and "Valid for 30 days ()".
          if (!promo || !promo.code) {
            return { status: promo && promo.eligible === false ? 'existing_multiple_orders' : 'error' };
          }
          return { status: s, code: promo.code, expires_at: promo.expires_at };
        });
      })
      .catch(function (err) {
        /* TASK-5998 — the error panel the shopper is about to see. */
        ilsFn('client_error', 'network', {
          form: 'popup', now: true,
          reason: String((err && err.message) || 'fetch_failed').slice(0, 60)
        });
        return { status: 'error' };
      });
  }

  function init() {
    var root = document.querySelector('[data-ils-email-popup]');
    if (!root) return;
    if (root.getAttribute('data-ils-eop-bound') === '1') return;
    root.setAttribute('data-ils-eop-bound', '1');

    var S = session();
    /* Touch the visit EXACTLY ONCE per page load, here. Everything after this
       reads without writing: a gate that wrote on every evaluation generated
       cross-tab `storage` traffic, and that echo is what cancelled this
       popup's own open timer (see scripts/task-5999-patch-crosstab.py). */
    try { S.state(); } catch (e) {}

    var enabled = root.getAttribute('data-enabled') === 'true';
    /* TASK-5999 — 10 s, and 10 is also the fallback: a cached pre-5999 render
       of the section carries data-delay-seconds="8", but a MISSING or
       unparseable attribute must not collapse to 0 and fire instantly (the
       pre-5999 `|| 0` did exactly that). */
    var delaySec = parseInt(root.getAttribute('data-delay-seconds'), 10);
    if (isNaN(delaySec) || delaySec < 0) delaySec = 10;
    var dismissHours = parseInt(root.getAttribute('data-dismiss-hours'), 10);
    if (isNaN(dismissHours) || dismissHours <= 0) dismissHours = DEFAULT_DISMISS_HOURS;
    var dismissMs = dismissHours * 60 * 60 * 1000;
    var excludePaths = parseExcludePaths(root.getAttribute('data-exclude-paths') || '');
    var marketCode = (root.getAttribute('data-ils-market-code') || 'ENEU').toUpperCase();
    // TASK-5839 — the raw facts, forwarded untouched so the server can
    // validate the combo instead of trusting it. Absent on a cached
    // pre-5839 render, in which case the server falls back to honouring
    // the legacy combo rather than refusing the shopper a code.
    var ilsCtx = {
      country: (root.getAttribute('data-ils-country') || '').toUpperCase(),
      language: (root.getAttribute('data-ils-language') || '').toLowerCase(),
      marketHandle: root.getAttribute('data-ils-market-handle') || ''
    };

    /* TASK-5998 — the market facts the section already read, handed to the
       telemetry once.
       TASK-6005 — ...and re-handed whenever the open's provenance changes, by
       publishOrigin() below. `source` is the only field that ever differs. */
    function publishFunnelCtx(src) {
      ilsFnCtx(FORM, {
        source: src,
        lang_domain: marketCode,
        country: ilsCtx.country || null,
        language: ilsCtx.language || null,
        template: (window.ILS_PAGE && window.ILS_PAGE.template) || null
      });
    }
    publishFunnelCtx('popup');

    /* ------------------------------------------------------------------
       TASK-6005 — THE OPEN'S PROVENANCE. See the file header.

       One variable. Written ONLY by publishOrigin(), which also republishes
       the funnel context so the telemetry and the request can never disagree
       about which source this instance is. Read at submit time, and nowhere
       else.
       ------------------------------------------------------------------ */
    var openOrigin = 'popup';
    function publishOrigin(o) {
      /* An allowlist, not a pass-through. Anything that is not the one known
         provenance is 'popup' — the backend answers 400 invalid_source to a
         name it does not know, so forwarding an unknown origin would turn a
         shopper's click into a failed claim. */
      var next = (o === 'pdp_widget') ? 'pdp_widget' : 'popup';
      if (next === openOrigin) return;
      openOrigin = next;
      publishFunnelCtx(next);
    }
    /* Read-only seam for the live verification harness (TASK-6005). It cannot
       set the origin — only report it. */
    root.ilsEopOrigin = function () { return openOrigin; };

    var backdrop = root.querySelector('[data-ils-eop-backdrop]');
    var closeBtn = root.querySelector('[data-ils-eop-close]');
    var form = root.querySelector('[data-ils-eop-form]');
    var input = root.querySelector('[data-ils-eop-input]');
    var submitBtn = root.querySelector('[data-ils-eop-submit]');
    var top = root.querySelector('[data-ils-eop-top]');
    var formWrap = root.querySelector('[data-ils-eop-form-wrap]');
    var result = root.querySelector('[data-ils-eop-result]');

    /* TASK-5999 — every pending timer this page view owns, so navigation or
       unload drops all of them. A fired-but-deferred auto-show is a CHAIN of
       setTimeouts, which is exactly the thing that leaks otherwise. */
    var timers = [];
    var cancelled = false;
    function later(fn, ms) {
      var id = setTimeout(function () { if (!cancelled) fn(); }, ms);
      timers.push(id);
      return id;
    }
    function cancelAll() {
      cancelled = true;
      /* TASK-6005 — the page is going away; drop the mark with it. Belt and
         braces: the variable is a closure local and cannot survive the page
         anyway, but a bfcache restore re-runs `pageshow`, not init(). */
      publishOrigin('popup');
      for (var ti = 0; ti < timers.length; ti++) { try { clearTimeout(timers[ti]); } catch (e) {} }
      timers.length = 0;
    }
    window.addEventListener('pagehide', cancelAll);
    window.addEventListener('beforeunload', cancelAll);

    function isOpen() { return !root.hidden; }

    function open(how) {
      root.hidden = false;
      requestAnimationFrame(function () { root.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      /* TASK-5998 — the popup is now ACTUALLY on screen. This is the popup's
         exposure denominator; it has never existed, which is why the surface
         responsible for half of all issuance was also the only one whose
         conversion could not be computed. */
      ilsFn('popup_shown', how === 'manual' ? 'manual' : 'auto', { form: 'popup', now: true });
    }
    /* TASK-5999 — `dismiss` true means the visitor (or a resolved result
       panel) closed it, so the 24-hour cadence starts NOW. This replaces
       suppressFor(resurface_days); the 14-day rule is gone. */
    function close(dismiss) {
      /* TASK-6005 — every way out of the popup funnels through here: the X,
         the backdrop, the click-outside-on-root path, Escape, and the result
         panel's own X. Resetting here is therefore the whole of requirement
         "clear the origin on close", and the automatic popup that may open
         later on this page view starts from 'popup' again. */
      publishOrigin('popup');
      root.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(function () { root.hidden = true; }, 300);
      if (dismiss) { try { S.markDismissed(); } catch (e) {} }
    }

    /* TASK-5998 — a dismissal is a measured refusal, not an error. The ways
       out are counted apart because they mean different things: the X is
       deliberate, the backdrop is often accidental.
       TASK-5999 — all FOUR close paths now record the DISMISSAL (the 24-hour
       cadence), and the two the requirement names but the code did not have
       are added below: click-outside, which reports as the backdrop it is,
       and Escape, which closes and records but emits no funnel row because
       that vocabulary has no slot for it yet — see the note there. */
    closeBtn.addEventListener('click', function () {
      ilsFn('popup_dismissed', 'close_button', { form: 'popup', now: true });
      close(true);
    });
    backdrop.addEventListener('click', function () {
      ilsFn('popup_dismissed', 'backdrop', { form: 'popup', now: true });
      close(true);
    });
    /* `.ils-eop__backdrop` is display:none in this section's CSS — the dim and
       blur live on .ils-eop-root itself — so the click-outside path has to be
       read off the root. e.target === root is true ONLY for the dimmed area
       around the card, never for the card or anything inside it. */
    root.addEventListener('click', function (e) {
      if (e.target !== root) return;
      /* This IS the backdrop in TASK-5998's vocabulary — the dim area around
         the card — so it reports under the outcome that already exists, with
         the implementation detail recorded beside it. */
      ilsFn('popup_dismissed', 'backdrop', { form: 'popup', now: true, detail: { via: 'root_outside_click' } });
      close(true);
    });
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key !== 'Escape' && e.key !== 'Esc' && e.keyCode !== 27) return;
      /* NO telemetry event here, deliberately. TASK-5998's popup_dismissed
         vocabulary is CLOSED (['close_button','backdrop'], mirrored server-side
         in db_funnel.js) and an unknown outcome is refused at the call site and
         dropped at ingest — so inventing 'escape' would log a console warning
         on every Escape press and record nothing. The DISMISSAL ITSELF is
         recorded, in the shared store, which is what requirement 3 asks for;
         only the funnel row is missing. Adding an 'escape' outcome needs the
         client vocabulary and its server mirror changed together, which is
         TASK-5998's file and TASK-5998's call. See REPORT-5999. */
      close(true);
    });

    function showResult(data) {
      top.hidden = true;
      formWrap.hidden = true;
      result.hidden = false;

      window.ILSEmailResult.render(result, data, { variant: 'popup' });

      // Close-X on the result panel — the top-strip X is hidden with the
      // strip, so we add a fresh one here so users can still dismiss.
      var xClose = document.createElement('button');
      xClose.type = 'button';
      xClose.className = 'ils-eop__close on-white';
      xClose.setAttribute('aria-label', 'Close');
      xClose.style.position = 'absolute';
      xClose.style.top = '10px';
      xClose.style.right = '10px';
      xClose.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      xClose.addEventListener('click', function () { close(true); });
      result.appendChild(xClose);
    }

    var submitLabel = submitBtn ? submitBtn.textContent : '';
    function busy() {
      if (!submitBtn) return;
      submitBtn.disabled = true;
      /* TASK-2603 — localized "Checking…" from the shared data-ils-eresult-i18n
         block (sections/custom-email-popup.liquid); English fallback. */
      submitBtn.textContent = (function(){try{var _e=document.querySelector('[data-ils-eresult-i18n]');if(_e&&_e.textContent){var _p=JSON.parse(_e.textContent);if(_p&&_p.checking)return _p.checking;}}catch(_x){}return 'Checking…';})();
    }
    /* TASK-5977 — the button must come BACK while the confirmation panel is
       open, or a visitor who corrects their address cannot resubmit. */
    function idle() {
      if (!submitBtn) return;
      submitBtn.disabled = false;
      if (submitLabel) submitBtn.textContent = submitLabel;
    }

    function finish(data) {
      showResult(data);
      /* TASK-5999 — a resolved submit ends the nagging for 24 hours whatever
         the outcome was, including a backend error: the visitor has engaged,
         and a failing backend must not re-open this on the next page. This
         replaces suppressFor(resurface_days). */
      try { S.markDismissed(); } catch (e) {}
      /* TASK-5999 — the PERMANENT flag now means exactly one thing: a code
         string really came back. NOT "the submit resolved" (the TASK-2450
         rule, which also fired for existing_multiple_orders — an address that
         gets no code at all), not "an email was typed", not an error. The
         other two surfaces apply the identical test in
         assets/custom-email-forms.js. */
      if (data && typeof data.code === 'string' && data.code.length > 0) {
        /* TASK-6005 — deliberately the SURFACE and not `openOrigin`. This
           writes to window.ILSEmailSession, which is localStorage and shared
           across tabs; putting the provenance there is exactly how the mark
           would escape this page view. The flag's only job is "this browser
           has a code", and it is read as a boolean. */
        try { S.markIssued(FORM); } catch (e) {}
      }
      /* TASK-6005 — the interaction has terminated (ILSEmailResult.render has
         already emitted client_result with endAttempt, under this origin), so
         drop the mark. A shopper who submits again on this page without
         pressing the widget CTA again is source 1, which is correct: the only
         thing that makes an attempt source 4 is a CTA press. */
      publishOrigin('popup');
    }

    /* TASK-5977 — the confirmation panel mounts in the popup's own white
       bottom pad, directly under the input, so the question appears where the
       visitor is already looking and the card never grows a second dialog. */
    function verifyMount() {
      return formWrap || root.querySelector('[data-ils-eop-form-wrap]') || root;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = input.value.trim();
      if (!email) {
        ilsFn('form_submit', 'empty_input', { form: 'popup' });
        return;
      }
      /* TASK-5998 — a real press. See custom-email-forms.js for why this is
         the number the owner was missing. */
      ilsFn('form_submit', 'pressed', { form: 'popup', now: true });
      busy();

      /* ------------------------------------------------------------------
         TASK-5977 — THE SHARED TYPO / CONFIRMATION GATE, FIRST.

         The SAME module, UI and localized strings the footer and homepage
         forms use (assets/custom-email-verify.js). It runs before
         /api/popup/email-status, before the order-count lookup, before
         FO10/SO05 selection, before issuance and before any letter — so a
         mistyped address never produces a code, a profile or an email. The
         popup additionally must not record a "submitted" flag for an address
         the visitor is still editing, which is why neither suppressFor() nor
         markSubmittedPermanent() is reached until the gate settles.
         ------------------------------------------------------------------ */
      var gate = (window.ILSEmailVerify && window.ILSEmailVerify.gate)
        /* TASK-6005 — `source` is the provenance the gate forwards to
           /api/popup/email-check and reports as the row's source; `form` is
           the surface it keys the attempt and its funnel rows by. Before
           TASK-6005 the gate had one option doing both jobs, which is fine
           while the two are always equal and wrong the moment they are not. */
        ? window.ILSEmailVerify.gate({ container: root, input: input, email: email, source: openOrigin, form: FORM, mount: verifyMount(), onAsk: idle })
        : (ilsFn('typo_gate', 'skipped', { form: FORM, reason: 'asset_missing' }),
          Promise.resolve({ email: email, confirmed: false }));

      gate.then(function (settledAddr) {
        if (!settledAddr) { idle(); return; }
        var finalEmail = settledAddr.email;
        var confirmed = settledAddr.confirmed === true;
        busy();
        /* TASK-2545 ils-tracker — link this email to the first-party visitor id
           (fire-and-forget; never affects the existing UX). */
        try { if (window.ILSTracker && window.ILSTracker.identify) window.ILSTracker.identify(finalEmail, openOrigin); } catch (e) {}
        /* TASK-6005 — THE ONE LINE THAT PRODUCES SOURCE 4. `openOrigin` is
           'pdp_widget' only for an instance the widget CTA opened. */
        runFlow(finalEmail, openOrigin, marketCode, ilsCtx, confirmed).then(function (data) {
          /* TASK-5977 — the SERVER asked a question the pre-check did not.
             Same panel, driven by the server's own body; once answered, the
             whole flow runs again for the settled address so the order checks
             and the FO10/SO05 decision are made on the address really used. */
          if (data && data.status === 'unconfirmed' && window.ILSEmailVerify && window.ILSEmailVerify.fromServer) {
            idle();
            window.ILSEmailVerify.fromServer({
              onAsk: idle,
              container: root, mount: verifyMount(), input: input,
              source: openOrigin, form: FORM, email: finalEmail, payload: data.unconfirmed
            }).then(function (again) {
              if (!again) return;
              busy();
              runFlow(again.email, openOrigin, marketCode, ilsCtx, again.confirmed === true)
                .then(function (d2) {
                  finish((d2 && d2.status === 'unconfirmed') ? { status: 'error' } : d2);
                });
            });
            return;
          }
          finish((data && data.status === 'unconfirmed') ? { status: 'error' } : data);
        });
      }, function () { idle(); });
    });

    /* ====================================================================
       TASK-5999 — THE AUTOMATIC-SHOW GATE.

       Named reasons, in the requirement's own priority order (identity >
       path > per-visit > dismissal cadence), and — this is the part that
       matters — evaluated TWICE: once to decide whether to arm the
       10-second timer at all, and again in the instant before the popup
       opens. Between those two moments the visitor may have taken a code in
       another tab, or dismissed it there, which is what the second
       evaluation is for. Requirement 2: "checks eligibility/dismissal state
       immediately before opening".
       ==================================================================== */
    /* `opts.afterOwnClaim` skips the per-visit test, and ONLY that test.
       It is used by the one caller that runs after this page view has already
       claimed the visit's appearance: at that point `shownVisit === visitId`
       is this page's OWN doing, so grading it would make the gate refuse its
       own claim — the state would say "shown" and the screen would show
       nothing. (It did, on Dev, before this guard existed.) What that caller
       still has to catch is what ANOTHER TAB can have done in the meantime,
       which is only "a code was issued" or "it was dismissed"; both are still
       checked. */
    function blockedReason(opts) {
      if (!enabled) return 'template_not_eligible';
      if (isPathExcluded(excludePaths)) return 'path_excluded';
      var st;
      try { st = S.state(false); } catch (e) { return null; }
      if (st.issued) return 'code_already_issued';
      if (!(opts && opts.afterOwnClaim) && st.shownVisit && st.shownVisit === st.visitId) {
        return 'already_shown_this_visit';
      }
      try { if (S.dismissedWithin(dismissMs)) return 'dismissed_within_window'; } catch (e) {}
      return null;
    }

    /* Exposed for QA only — read-only, and it calls the SAME predicate the
       live gate uses, so a probe can never be told something the gate does
       not believe. */
    root.ilsEopDebug = function () {
      var st = {};
      try { st = S.state(false); } catch (e) {}
      return {
        task: 'TASK-5999',
        enabled: enabled,
        template: root.getAttribute('data-ils-template') || '',
        delaySeconds: delaySec,
        dismissHours: dismissHours,
        storageTier: st.tier || 'none',
        blockedReason: blockedReason(),
        momentIsWrong: momentIsWrong(),
        state: st,
        open: isOpen()
      };
    };

    /* -----------------------------------------------------------------
       TASK-5998 — WHY THE POPUP DID OR DID NOT SHOW, RECORDED.

       The owner's question is whether popup REACH is suppressing requests.
       REPORT-5994 could only say that the popup rendered on the homepage
       (5-6 % of pageviews) and that its gate had five independent skip
       reasons; it could not say how often each one fired, because none of
       them was recorded anywhere.

       TASK-5999 kept this event and re-pointed it at the new policy: the
       outcome is now the gate's own named reason (one of
       disabled / path_excluded / code_already_issued /
       already_shown_this_visit / dismissed_within_window / eligible) and the
       detail carries the two knobs that still exist. The retired
       resurface_days / max_total_shows / shows_so_far fields are gone with
       the rules they described.
       ----------------------------------------------------------------- */
    /* The outcome vocabulary is TASK-5998's and it is CLOSED, so each new
       reason reports under the existing outcome it SUCCEEDS, and the exact
       TASK-5999 reason travels in detail.reason where nothing validates it:
         code_already_issued       -> submitted_permanent  (the flag it replaces)
         already_shown_this_visit  -> show_cap             (a cap, now per visit)
         dismissed_within_window   -> resurface_timer      (the same timer, 24 h)
       Changing the vocabulary itself would mean editing custom-funnel-telemetry.js
       AND its server mirror db_funnel.js, both TASK-5998's, mid-flight. */
    var GATE_OUTCOME = {
      template_not_eligible: 'disabled',
      path_excluded: 'path_excluded',
      code_already_issued: 'submitted_permanent',
      already_shown_this_visit: 'show_cap',
      dismissed_within_window: 'resurface_timer'
    };
    var firstReason = blockedReason();
    ilsFn('popup_gate', firstReason === null ? 'eligible' : (GATE_OUTCOME[firstReason] || 'disabled'), {
      form: 'popup',
      now: true,
      detail: {
        reason: firstReason || 'eligible',
        policy: 'TASK-5999',
        delay_seconds: delaySec,
        dismiss_hours: dismissHours,
        template: root.getAttribute('data-ils-template') || null,
        storage_tier: (function () { try { return S.state(false).tier; } catch (e) { return 'none'; } })(),
        will_show: firstReason === null
      }
    });

    function attemptOpen(tries) {
      if (cancelled) return;
      /* TASK-6005 — THE 10 s TIMER vs THE WIDGET CTA.

         The manual path deliberately does not claim this visit's one
         automatic appearance (TASK-5999), so this timer can still fire while a
         CTA-opened popup is on screen. Before TASK-6005 it then called
         open('auto') on the already-open card: a second popup_shown row for a
         card that never re-appeared, and — now that an open carries a
         provenance — it would also have reset the mark under a shopper who was
         mid-typing in the popup the CTA opened, turning their source-4 claim
         into source 1. Standing down is the correct answer in both readings:
         the shopper is already looking at the popup. */
      if (isOpen()) return;
      /* An automatic open is source 1, always. Whatever a previous widget open
         on this page view left behind, it does not reach this one. */
      publishOrigin('popup');
      /* SECOND evaluation — immediately before opening. */
      if (blockedReason()) return;
      if (momentIsWrong()) {
        /* Not a refusal: the page is hidden, or the cart drawer is open and
           requirement 1 bans the popup over it. Come back in a second and
           leave this visit's appearance unspent. */
        if (tries >= DEFER_MAX_TRIES) return;
        later(function () { attemptOpen(tries + 1); }, DEFER_POLL_MS);
        return;
      }
      var token;
      try { token = S.claimVisitShow(); } catch (e) { token = null; }
      if (!token) return;  /* another tab already took this visit's appearance */
      /* Last-write-wins arbitration between tabs that fired together: whoever
         wrote the surviving token opens, the other stands down. A single tab
         always confirms its own token, so the cost is 60 ms. */
      later(function () {
        var ok = false;
        try { ok = S.confirmVisitShow(token); } catch (e) { ok = true; }
        /* Lost a simultaneous race: the other tab holds the live claim, so
           there is nothing of ours to give back. */
        if (!ok) return;
        /* A decision made elsewhere in the meantime (a code issued, or a
           dismissal, in another tab). Give the claim back — it was never
           spent on a popup — and stop. */
        if (blockedReason({ afterOwnClaim: true })) {
          try { S.releaseVisitShow(token); } catch (e) {}
          return;
        }
        /* The moment turned wrong inside the 60 ms window (the cart drawer
           opened, the tab went to the background). Give the claim back and go
           on waiting, exactly as if it had been wrong at fire time. */
        if (momentIsWrong()) {
          try { S.releaseVisitShow(token); } catch (e) {}
          if (tries < DEFER_MAX_TRIES) later(function () { attemptOpen(tries + 1); }, DEFER_POLL_MS);
          return;
        }
        /* TASK-6005 — the CTA can be pressed inside this 60 ms arbitration
           window. Give the claim back (it was never spent on a popup) and
           leave the widget's open, and its mark, alone. */
        if (isOpen()) {
          try { S.releaseVisitShow(token); } catch (e) {}
          return;
        }
        /* And once more immediately before the open, for the same reason as at
           the top of attemptOpen: an automatic popup is source 1. */
        publishOrigin('popup');
        open('auto');
      }, 60);
    }

    if (firstReason === null) {
      later(function () { attemptOpen(0); }, delaySec * 1000);
    }

    /* Another tab issued a code or dismissed the popup while our timer was
       pending -> drop the timer rather than open over a decision already made.
       It NEVER closes an open popup: a visitor mid-typing must not have the
       card pulled out from under them.

       `afterOwnClaim` is ESSENTIAL here, not an optimisation. Without it this
       handler stands down for 'already_shown_this_visit' — which, in the 60 ms
       between this page claiming the appearance and opening it, is this page's
       OWN claim. The measured result was a claim with no popup, in both tabs,
       every time there were two (see scripts/task-5999-patch-crosstab.py). A
       per-visit claim is never a reason for the claimant to cancel itself. */
    try {
      S.onChange(function () {
        if (isOpen()) return;
        if (blockedReason({ afterOwnClaim: true })) cancelAll();
      });
    } catch (e) {}

    /* Manual opener. Two callers, one event, no second dialog:
         - ils-devcheck-v2.js -> Popup tab, with no detail — a preview tool;
         - assets/custom-pdp-promo.js (TASK-6003), with
           detail.origin === 'pdp_widget' (TASK-6005) — the product-page
           discount widget's "Get code" button.
       It still works regardless of page / delay / suppression state, and it
       still neither claims this visit's one automatic appearance nor counts as
       one. */
    document.addEventListener('ils:email-popup:show', function (e) {
      /* TASK-6005 — already on screen: do nothing at all. Re-opening an open
         popup emitted a duplicate popup_shown row, and it would let a press
         re-label a popup that had opened BY ITSELF (page type never decides
         the source, and neither does a click aimed at an overlay). */
      if (isOpen()) return;
      var origin = null;
      try { origin = e && e.detail ? e.detail.origin : null; } catch (x) { origin = null; }
      /* Allowlisted inside publishOrigin: an unknown origin is 'popup'. */
      publishOrigin(origin);
      open('manual');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
